"use client";

import { ICourse } from "@/api/admin/course";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn, isEnabledUnlessExplicitlyOff } from "@/lib/utils";
import { hasSeenPromotionalBanner } from "@/sessionStorage";
import {
	setChatbotEnabled,
	setCourseLeadDropData,
	setFloatingIconsVisibility,
	setPageAuthor,
	setPageCategory,
	setPromotionalBannerOpen,
	setTrackingEnabled,
} from "@/store/slices/globalslice";
import { handleSetIsPreview, handleSetShowLeaddrop } from "@/store/slices/leaddrop";
import { setLeadFormConfig } from "@/store/slices/leaddropform";
import type { IPageAuthor } from "@/types/author";
import RichTextEditor1 from "@/widgets/richtexteditor/RichTextEditor1";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { startTransition, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
	PAGE_TYPES,
	PROGRAM_CARDS_VARIANTS,
	SECTION_TYPES,
	SECTION_VARIANTS,
} from "../../constants";
// ─── RUDDERSTACK (new, sheet-aligned) auto-instrumentation ──────────────────
// Centralised widget_impression + cta_click for every section, added here so
// individual widgets need NO per-widget tracking code. Delete these three
// imports + their use below (and the customAnalytics files) to fully revert.
import CtaClickDelegate from "@/customAnalytics/CtaClickDelegate";
import TrackedWidget from "@/customAnalytics/TrackedWidget";
import { resolveWidgetName } from "@/customAnalytics/sectionSlugs";

const BlogWithLead1 = dynamic(
	() => import("@/widgets/blog/BlogWithLead1"),
	{
		ssr: false,
		loading: () => <div className="min-h-[500px]" />,
	}
);

/**
 * Stable React key for a rendered section. Sections expanded out of a Widget
 * Block keep their real page_component id, so the same block placed twice on a
 * page would repeat it — the server sends `render_key`, unique per placement.
 */
function componentKey(component: any): string | undefined {
	const key = component?.render_key ?? component?.id;
	return key != null ? String(key) : undefined;
}

function isTruthyStatus(data: any) {
	if (!data) return false;
	const s = data.status;
	return s === true || s === 1 || s === "1";
}

function mobileAppBlogColumnEnabled(componentData: any) {
	const row = componentData?.data ?? componentData;
	const b = row?.is_blog;
	return b === true || b === 1 || b === "1";
}

function isBlogMobileAppSlot(c: any) {
	return (
		String(c.section) === SECTION_TYPES.MOBILE_APP &&
		isTruthyStatus(c.data) &&
		mobileAppBlogColumnEnabled(c.data)
	);
}

function isBlogInlineImageSlot(c: any) {
	return (
		String(c.section) === SECTION_TYPES.BLOG_INLINE_IMAGE &&
		isTruthyStatus(c.data)
	);
}

/**
 * Author Card (3003) / Author Byline (3004). Both are article-width in the design,
 * so when the blog page HAS an article column they join it alongside the breadcrumb
 * and inline images. See `isArticleColumnAuthorSlot` — without article content they
 * must stay in `tail` and keep the editor's ordering. Each widget still self-gates
 * on "blog page + linked author" and renders null otherwise.
 */
function isBlogAuthorSlot(c: any) {
	const s = String(c.section);
	return (
		(s === SECTION_TYPES.AUTHOR_CARD || s === SECTION_TYPES.AUTHOR_BYLINE) &&
		isTruthyStatus(c.data)
	);
}

/** Header + nav must stay at the top; blog layout otherwise moves non-band sections to `tail` (below content). */
function isBlogChromeSection(c: any) {
	const s = String(c.section);
	return (
		(s === SECTION_TYPES.HEADER || s === SECTION_TYPES.MENU_ITEMS) &&
		isTruthyStatus(c.data)
	);
}

const NewPage = ({
	pageData,
	courseErpData,
	components,
	pageCategory,
	show_lead_drop,
}: {
	pageData: any;
	courseErpData: ICourse;
	components: any;
	pageCategory: string;
	show_lead_drop: boolean;
}) => {
	const {
		enable_mobile_whatsapp_icon: isWhatsAppIconEnabled,
		enable_phone_icon: isPhoneIconEnabled,
		enable_promotional_banner: isPromotionalBannerEnabled,
		enable_promotional_floating_icon: pagePromotionalFloatingIcon,
		enable_promotional_icon_blink: pagePromotionalIconBlink,
		tracking_enabled: pageTrackingEnabled,
		chatbot_enabled: pageChatbotEnabled,
		// Author linked to this page (server: page.author). NULL when unlinked or the
		// author is unpublished — widgets must treat it as optional.
		author: pageAuthor,

		// Resolved server-side (page pin → course → university default) and shipped WITH the
		// page. `null` — the steady state everywhere today — means the legacy variant renders.
		// One per SURFACE: a page may show an embedded form AND open a popup, and they are
		// independent choices resolved independently.
		custom_lead_form: pageCustomForm,
		custom_lead_form_popup: pageCustomFormPopup,
		custom_lead_form_source: pageCustomFormSource,
	} = pageData as { author?: IPageAuthor | null;[key: string]: any };

	/** Page-level article meta for the Author Byline (3004). Memoised so the widget
		does not re-render on every NewPage render from a fresh object identity. */
	const pageMeta = useMemo(
		() => ({
			custom_blog_updated_date:
				(pageData as any)?.custom_blog_updated_date ?? null,
			updated_at: (pageData as any)?.updated_at ?? null,
			created_at: (pageData as any)?.created_at ?? null,
			read_time: (pageData as any)?.read_time ?? null,
			views: (pageData as any)?.views ?? null,
		}),
		[pageData],
	);

	const dispatch = useDispatch();
	const pathname = usePathname();

	const isBlogPage =
		String(pageCategory ?? "").toLowerCase() === PAGE_TYPES.BLOG.toLowerCase();

	/* Only the blog layout below reads `isMobile`. Left unconditional, the hook's
	 * null → boolean update after mount re-renders EVERY widget on every page for
	 * nothing — and a re-render while chunks are still loading is what triggers
	 * the Suspense fallback. `enabled` keeps it inert off the blog path.
	*/
	const isMobile = useIsMobile(768, isBlogPage);

	/* Floating promo icon (the FAB that re-opens the promotional banner). NULL/undefined/
	 * true → on, so every already-live page keeps its icon without any admin action; only
	 * an explicit false hides it. The API serves the raw column, i.e. tinyint 0/1 — hence
	 * the helper, not `!== false`. FloatingIcons still requires the page's promotional
	 * banner to be enabled before this can show anything.
	*/
	const isPromotionalFloatingIconEnabled = isEnabledUnlessExplicitlyOff(
		pagePromotionalFloatingIcon,
	);

	/* Blink of that icon. Same NULL/undefined/true → on contract, so already-live pages
	   keep pulsing until an admin turns it off. Independent of the visibility switch
	   above: off leaves the icon rendered and clickable, just perfectly still. */
	const isPromotionalIconBlinkEnabled = isEnabledUnlessExplicitlyOff(
		pagePromotionalIconBlink,
	);

	useEffect(() => {
		const isPreview = pathname.includes("/admin");

		/* Non-urgent on purpose. None of this feeds the first paint — it's state
		 * for CTAs, popups and floating icons. Dispatching it urgently re-renders
		 * every widget while their chunks are still in flight, and re-rendering a
		 * widget that hasn't hydrated yet is exactly what makes React drop the
		 * server HTML and show a Suspense fallback. Inside a transition React
		 * keeps the server-rendered DOM on screen and applies this once the tree
		 * is ready, so the page doesn't visibly render a second time.
		*/
		startTransition(() => {
			dispatch(handleSetIsPreview(isPreview));
			dispatch(setCourseLeadDropData(courseErpData as ICourse));
			dispatch(handleSetShowLeaddrop(show_lead_drop));
			dispatch(setPageCategory(pageCategory?.toLowerCase()?.split(" ")[0] ?? ""));

			/* Pops open on the first page of the session only. Once dismissed, later
			   pages leave it closed and surface the floating promo icon instead. */
			dispatch(setPromotionalBannerOpen(Boolean(isPromotionalBannerEnabled) && !hasSeenPromotionalBanner()),);

			dispatch(
				setFloatingIconsVisibility({
					isWhatsAppIconEnabled: isWhatsAppIconEnabled,
					isPhoneIconEnabled: isPhoneIconEnabled,
					isPromotionalBannerEnabled: isPromotionalBannerEnabled,
					isPromotionalFloatingIconEnabled: isPromotionalFloatingIconEnabled,
					isPromotionalIconBlinkEnabled: isPromotionalIconBlinkEnabled,
				}),
			);
		});

		// Page-level master analytics switch → off suppresses ALL tracking for the
		// page (gated in rudderStackEvents.sendEvent). NULL/undefined/true → enabled,
		// so already-live pages stay tracked without any admin action. The API serves
		// the raw column, i.e. tinyint 0/1 — hence the helper, not `!== false`.
		dispatch(setTrackingEnabled(isEnabledUnlessExplicitlyOff(pageTrackingEnabled)));

		// Page-level chatbot switch → off keeps the chatbot off for this page (gated
		// in uniConfig/scripts/PageScript). NULL/undefined/true → enabled, so
		// already-live pages keep their chatbot without any admin action. The API
		// serves the raw column, i.e. tinyint 0/1 — hence the helper, not `!== false`.
		dispatch(setChatbotEnabled(isEnabledUnlessExplicitlyOff(pageChatbotEnabled)));
		// Published in the store so nested components (not just top-level widgets) can
		// read the author without prop drilling. Mirrors the `author` prop below.
		dispatch(setPageAuthor((pageAuthor as IPageAuthor | null) ?? null));

		// Custom lead form for this page. Dispatched unconditionally — including the null
		// case — so navigating from a page WITH a config to one without falls back to the
		// legacy form instead of leaving the previous page's form on screen.
		dispatch(
			setLeadFormConfig({
				config: pageCustomForm ?? null,
				popupConfig: pageCustomFormPopup ?? null,
				source: pageCustomFormSource ?? "legacy",
			}),
		);

		dispatch(
			setFloatingIconsVisibility({
				isWhatsAppIconEnabled: isWhatsAppIconEnabled,
				isPhoneIconEnabled: isPhoneIconEnabled,
				isPromotionalBannerEnabled: isPromotionalBannerEnabled,
				isPromotionalFloatingIconEnabled: isPromotionalFloatingIconEnabled,
				isPromotionalIconBlinkEnabled: isPromotionalIconBlinkEnabled,
			}),
		);
	}, [
		dispatch,
		pathname,
		courseErpData,
		show_lead_drop,
		pageCategory,
		isPromotionalBannerEnabled,
		isPromotionalFloatingIconEnabled,
		isPromotionalIconBlinkEnabled,
		isPhoneIconEnabled,
		isWhatsAppIconEnabled,
		pageTrackingEnabled,
		pageChatbotEnabled,
		pageAuthor,
		pageCustomForm,
		pageCustomFormPopup,
		pageCustomFormSource,
	]);

	if (!components?.length) return null;

	const renderComponent = (
		component: any,
		index: number,
		extraProps = {},
		wrap = true,
	) => {
		const { section, variation, data } = component;
		const Component = SECTION_VARIANTS[section]?.[variation];

		if (!Component || !data?.status) return null;

		const rendered = (
			<Component
				pageCategory={pageCategory}
				key={componentKey(component) || `${section}-${variation}-${index}`}
				data={data}
				widgetOrder={index + 1}
				author={pageAuthor ?? null}
				pageMeta={pageMeta}
				{...extraProps}
			/>
		);

		// Auto-instrumentation is ON by default for every rendered section (standard
		// AND blog). Safe on the blog flex/sticky columns because TrackedWidget is
		// `display: contents` — it adds NO layout box, so wrapping is layout-identical
		// to no-wrapper. Pass wrap=false only to deliberately skip a call site.
		if (!wrap) return rendered;

		// Per-widget admin toggle (default ON) + resolved widget_name
		// (admin tracking_name → section default → `section_<code>`). tracking_enabled
		// / tracking_name live on the page_component row; absent → tracked with the
		// default name, so this works before the admin controls ship. The row is served
		// raw, so tracking_enabled is tinyint 0/1 — `!== false` would never see an "off".
		const trackOn = isEnabledUnlessExplicitlyOff(component.tracking_enabled);
		const widgetName = resolveWidgetName(section, component.tracking_name);

		return (
			<TrackedWidget
				key={componentKey(component) || `track-${section}-${variation}-${index}`}
				trackOn={trackOn}
				widgetName={widgetName}
				widgetOrder={index + 1}
			>
				{rendered}
			</TrackedWidget>
		);
	};

	if (isBlogPage) {
		const isBlogProgramCardsColumn = (c: any) =>
			String(c.section) === SECTION_TYPES.PROGRAM_CARDS &&
			String(c.variation) === PROGRAM_CARDS_VARIANTS.PROGRAM_CARDS_BLOG_1;

		const heroBanners = components.filter(
			(c: any) =>
				String(c.section) === SECTION_TYPES.BANNER && isTruthyStatus(c.data),
		);
		const bodyRtes = components.filter(
			(c: any) =>
				String(c.section) === SECTION_TYPES.RICH_TEXT_EDITOR &&
				isTruthyStatus(c.data),
		);
		const firstBwl = components.find(
			(c: any) =>
				String(c.section) === SECTION_TYPES.BLOG_WITH_LEAD &&
				isTruthyStatus(c.data),
		);
		const blogChrome = components.filter(isBlogChromeSection);

		/**
		 * The author widgets only join the article column when that column actually
		 * has article content. Without an RTE / blog-with-lead the band is an empty
		 * shell rendered right after the hero, so hoisting them there would yank them
		 * to the top of the page instead of the slot the editor put them in — they
		 * stay in `tail` (document order) in that case.
		 */
		const hasArticleColumn = bodyRtes.length > 0 || Boolean(firstBwl);
		const isArticleColumnAuthorSlot = (c: any) =>
			hasArticleColumn && isBlogAuthorSlot(c);

		const tail = components.filter((c: any) => {
			const s = String(c.section);
			if (isBlogChromeSection(c)) return false;
			if (s === SECTION_TYPES.BANNER) return false;
			if (s === SECTION_TYPES.RICH_TEXT_EDITOR) return false;
			if (s === SECTION_TYPES.BLOG_WITH_LEAD) return false;
			if (isBlogProgramCardsColumn(c)) return false;
			if (isBlogMobileAppSlot(c)) return false;
			if (s === SECTION_TYPES.BREADCRUMB_BANNER && isTruthyStatus(c.data)) {
				return false;
			}
			if (s === SECTION_TYPES.BLOG_INLINE_IMAGE && isTruthyStatus(c.data)) {
				return false;
			}
			if (isArticleColumnAuthorSlot(c)) return false;
			return true;
		});

		/** Blog program cards + Mobile App (blog toggle), in page-builder order. */
		const blogBandMiddleSlots = components.filter(
			(c: any) =>
				(isBlogProgramCardsColumn(c) && isTruthyStatus(c.data)) ||
				isBlogMobileAppSlot(c),
		);

		const hasBlogMobileAppSlot = components.some(isBlogMobileAppSlot);

		const hasBlogBreadcrumbSlot = components.some(
			(c: any) =>
				String(c.section) === SECTION_TYPES.BREADCRUMB_BANNER &&
				isTruthyStatus(c.data),
		);

		const hasBlogInlineImageSlot = components.some(isBlogInlineImageSlot);

		const blogInlineSlotsFirstBranch = components.filter(isBlogInlineImageSlot);

		const blogBreadcrumbShellClass =
			"relative z-10 mx-auto w-full px-[24px] pt-10 pb-0 sm:max-w-[1080px] sm:pl-[60px] sm:pr-[40px] md:max-w-[1230px] md:pr-[60px] lg:max-w-[1430px] lg:pl-[80px] lg:pr-[70px] xl:max-w-[1750px] xl:px-[140px]";

		const blogArticleColumnClass =
			"w-full max-w-full sm:max-w-[500px] md:max-w-[620px] lg:max-w-[732px] xl:max-w-[800px]";

		/**
		 * Author widgets are article furniture and carry no gutters of their own — the
		 * article band supplies them. Rendered standalone in `tail` they would sit flush
		 * against the viewport edge, so they get the page shell here: full container
		 * width (NOT capped to the article column), just the standard page gutters.
		 */
		const blogAuthorStandaloneShellClass =
			"relative z-10 mx-auto w-full px-[24px] py-5 sm:max-w-[1080px] sm:px-[60px] md:max-w-[1230px] lg:max-w-[1430px] lg:px-[80px] xl:max-w-[1750px] xl:px-[140px]";

		const renderTailComponent = (c: any, i: number) => {
			if (!isBlogAuthorSlot(c)) return renderComponent(c, i);
			return (
				<div
					key={c.id || `blog-author-tail-${i}`}
					className={blogAuthorStandaloneShellClass}
				>
					{renderComponent(c, i)}
				</div>
			);
		};

		const blogBreadcrumbSlots = components.filter(
			(c: any) =>
				String(c.section) === SECTION_TYPES.BREADCRUMB_BANNER &&
				isTruthyStatus(c.data),
		);

		const blogAuthorSlots = components.filter(isArticleColumnAuthorSlot);
		const hasBlogAuthorSlot = blogAuthorSlots.length > 0;

		if (bodyRtes.length === 0 && firstBwl) {
			return (
				<>
					{/* Page-level capture listener for cta_click — mounted for blog pages too. */}
					<CtaClickDelegate />
					{blogChrome.map((c: any, i: number) => renderComponent(c, i))}
					{heroBanners.map((c: any, i: number) => renderComponent(c, i))}
					{blogBandMiddleSlots.map((c: any, i: number) =>
						renderComponent(c, i + 800),
					)}
					{blogBreadcrumbSlots.length > 0 ||
						blogInlineSlotsFirstBranch.length > 0 ||
						hasBlogAuthorSlot ? (
						<div className={blogBreadcrumbShellClass}>
							<div
								className={cn(
									blogArticleColumnClass,
									"flex min-w-0 flex-col gap-5",
								)}
							>
								{blogBreadcrumbSlots.map((c: any, i: number) =>
									renderComponent(c, i + 750),
								)}
								{blogInlineSlotsFirstBranch.map((c: any, i: number) => (
									<div
										key={componentKey(c) || `blog-inline-img-shell-${i}`}
										className="w-full min-w-0"
									>
										{renderComponent(c, i + 760)}
									</div>
								))}
								{blogAuthorSlots.map((c: any, i: number) => (
									<div
										key={c.id || `blog-author-shell-${i}`}
										className="w-full min-w-0"
									>
										{renderComponent(c, i + 770)}
									</div>
								))}
							</div>
						</div>
					) : null}
					<BlogWithLead1 data={firstBwl.data} />
					{tail.map((c: any, i: number) => renderTailComponent(c, i))}
				</>
			);
		}

		const leftColumnOrdered = components.filter((c: any) => {
			const s = String(c.section);
			if (s === SECTION_TYPES.RICH_TEXT_EDITOR && isTruthyStatus(c.data)) {
				return true;
			}
			if (s === SECTION_TYPES.BREADCRUMB_BANNER && isTruthyStatus(c.data)) {
				return true;
			}
			if (isBlogInlineImageSlot(c)) {
				return true;
			}
			if (isArticleColumnAuthorSlot(c)) {
				return true;
			}
			if (isBlogProgramCardsColumn(c) && isTruthyStatus(c.data)) {
				return true;
			}
			if (isBlogMobileAppSlot(c)) {
				return true;
			}
			return false;
		});
		const bwlRow = firstBwl ? firstBwl.data?.data ?? firstBwl.data : null;
		const firstRteRow = bodyRtes[0]
			? bodyRtes[0].data?.data ?? bodyRtes[0].data
			: null;
		const pickBlogBandColor = (row: any) => {
			if (!row || typeof row !== "object") return "";
			const band = String(row.article_band_background_color ?? "").trim();
			if (band) return band;
			return String(row.background_color ?? "").trim();
		};
		const bandBg =
			pickBlogBandColor(bwlRow) ||
			pickBlogBandColor(firstRteRow) ||
			"#FFFFFF";
		const bandPad =
			(firstBwl &&
				(bwlRow?.enable_padding === true ||
					bwlRow?.enable_padding === 1 ||
					bwlRow?.enable_padding === "1")) ||
			(!firstBwl && bodyRtes.length > 0);

		/** Mobile: same order as page builder (RTE / blog-with-lead interleaved). Desktop: unchanged two columns. */
		const bandMembersInOrder = components.filter((c: any) => {
			const s = String(c.section);
			if (s === SECTION_TYPES.RICH_TEXT_EDITOR) return isTruthyStatus(c.data);
			if (s === SECTION_TYPES.BREADCRUMB_BANNER) return isTruthyStatus(c.data);
			if (isBlogInlineImageSlot(c)) return true;
			if (isArticleColumnAuthorSlot(c)) return true;
			if (s === SECTION_TYPES.BLOG_WITH_LEAD) {
				return Boolean(firstBwl && c === firstBwl && isTruthyStatus(c.data));
			}
			if (isBlogProgramCardsColumn(c)) return isTruthyStatus(c.data);
			if (isBlogMobileAppSlot(c)) return true;
			return false;
		});

		return (
			<>
				{/* Page-level capture listener for cta_click — mounted for blog pages too. */}
				<CtaClickDelegate />
				{blogChrome.map((c: any, i: number) => renderComponent(c, i))}
				{heroBanners.map((c: any, i: number) => renderComponent(c, i))}
				{(bodyRtes.length > 0 ||
					firstBwl ||
					hasBlogMobileAppSlot ||
					hasBlogBreadcrumbSlot ||
					hasBlogInlineImageSlot ||
					hasBlogAuthorSlot) && (
						<section
							className={cn(
								"relative w-full",
								// bandPad &&
								// "py-10 sm:py-10 md:py-[50px] lg:py-[60px] xl:py-10",
							)}
							style={{ backgroundColor: bandBg }}
						>
							<div
								className={cn(
									"relative z-10 mx-auto w-full",
									/* Blog: 20px between stacked widgets / columns only (gap-5 = 20px). */
									"flex flex-col items-stretch gap-9 px-[24px] py-[40px]",
									"sm:max-w-[1080px] sm:flex-row sm:items-stretch sm:gap-9 sm:px-[60px]",
									"md:max-w-[1230px] md:pr-[60px]",
									"lg:max-w-[1430px] lg:px-[40px]",
									"xl:max-w-[1750px] xl:gap-9 xl:px-[140px] xl:py-[80px]",
								)}
							>

								{/* Mobile: document order from admin (RTE + lead where placed) */}
								<div
									className={cn(
										"flex w-full min-w-0 flex-col items-stretch gap-5",
										"min-w-0 w-full sm:w-auto sm:min-w-0 sm:flex-1 sm:self-start",
									)}
								>
									{bandMembersInOrder.map((c: any, i: number) => {
										if (String(c.section) === SECTION_TYPES.RICH_TEXT_EDITOR) {
											return (
												<RichTextEditor1
													key={componentKey(c) || `blog-rte-mobile-${i}`}
													data={c.data}
													blogColumn
												/>
											);
										}
										if (String(c.section) === SECTION_TYPES.BREADCRUMB_BANNER) {
											return (
												<div
													key={componentKey(c) || `blog-bc-mobile-${i}`}
													className="w-full min-w-0"
												>
													{renderComponent(c, i)}
												</div>
											);
										}
										if (isBlogInlineImageSlot(c)) {
											return (
												<div
													key={componentKey(c) || `blog-inline-img-mobile-${i}`}
													className="w-full min-w-0"
												>
													{renderComponent(c, i)}
												</div>
											);
										}
										if (isArticleColumnAuthorSlot(c)) {
											return (
												<div
													key={c.id || `blog-author-mobile-${i}`}
													className="w-full min-w-0"
												>
													{renderComponent(c, i)}
												</div>
											);
										}
										if (isBlogProgramCardsColumn(c)) {
											return (
												<div
													key={componentKey(c) || `blog-pc-mobile-${i}`}
													className="w-full min-w-0"
												>
													{renderComponent(c, i)}
												</div>
											);
										}
										if (isBlogMobileAppSlot(c)) {
											return (
												<div
													key={componentKey(c) || `blog-mobile-app-mobile-${i}`}
													className="w-full min-w-0"
												>
													{renderComponent(c, i)}
												</div>
											);
										}
										if (isMobile) {
											return (
												<div
													key={componentKey(c) || `blog-bwl-mobile-${i}`}
													className="flex sm:hidden w-full min-w-0 max-w-full flex-col items-stretch"
												>
													<BlogWithLead1 data={c.data} formColumnOnly />
												</div>
											);
										}

										return null;
									})}
								</div>

								{firstBwl && !isMobile ? (
									<aside
										className={cn(
											"hidden w-full min-w-0 flex-col sm:flex sm:w-[33%] sm:max-w-[33%] sm:flex-none sm:items-stretch",
										)}
									>
										<div
											className={cn(
												"sticky w-full sm:top-[135px] md:top-[160px] lg:top-[185px]",
												"flex flex-col items-center sm:items-stretch",
											)}
										>
											<BlogWithLead1
												data={firstBwl.data}
												formColumnOnly
											/>
										</div>
									</aside>
								) : null}
							</div>
						</section>
					)}
				{tail.map((c: any, i: number) => renderTailComponent(c, i))}
			</>
		);
	}

	return (
		<>
			{/* One page-level capture listener attributes every button/link click in a
          tracked section to its widget. Standard path only (blog path deferred). */}
			<CtaClickDelegate />
			{components.map((component: any, index: number) => {
				return renderComponent(component, index, {}, true);
			})}
		</>
	);
};

export default NewPage;
